import React, { useState, useEffect } from 'react';
import CustomModal from './CustomModal';

interface Cell {
  row: number;
  col: number;
}

interface CageModalProps {
  show: boolean;
  onHide: () => void;
  selectedCells: Cell[];
  onSave: (target: number, operation: string, cells: Cell[]) => void;
}

const CageModal: React.FC<CageModalProps> = ({ show, onHide, selectedCells, onSave }) => {
  const [targetNumber, setTargetNumber] = useState<number>(0);
  const [operation, setOperation] = useState<string>('');
  const [availableOperations, setAvailableOperations] = useState<string[]>([]);

  const isSingleCellCage = selectedCells.length === 1;

  useEffect(() => {
    const numCells = selectedCells.length;
    let ops: string[] = ['+'];

    if (numCells > 1) {
      ops.push('*');
    }
    if (numCells === 2) {
      ops.push('-');
      ops.push('/');
    }
    setAvailableOperations(ops);

    if (isSingleCellCage) {
      setOperation('='); // Implicit operation for single cell
    } else {
      // Reset operation if the current one is no longer valid
      if (!ops.includes(operation)) {
        setOperation(ops[0] || '');
      }
    }
    setTargetNumber(0); // Reset target number when modal opens
  }, [selectedCells, operation, isSingleCellCage]);

  const handleSave = () => {
    if (targetNumber > 0 && operation !== '') {
      onSave(targetNumber, operation, selectedCells);
      onHide();
      setTargetNumber(0);
      setOperation('');
    } else {
      alert('Please enter a target number and select an operation.');
    }
  };

  const handleNumberClick = (num: number) => {
    setTargetNumber(num);
  };

  const handleOperationClick = (op: string) => {
    setOperation(op);
  };

  const maxTargetNumber = isSingleCellCage ? 9 : (selectedCells.length > 0 ? selectedCells.length * 9 : 9);

  return (
    <CustomModal show={show} onClose={onHide} title="Create New Cage">
      <div className="mb-3">
        <h6 className="form-label">Target Number: <span className="fw-bold">{targetNumber === 0 ? '?' : targetNumber}</span></h6>
        <div className="d-flex flex-wrap">
          {Array.from({ length: isSingleCellCage ? 9 : maxTargetNumber }, (_, i) => i + 1).map((num) => (
            <button
              key={num}
              className={`btn btn-outline-primary m-1 ${targetNumber === num ? 'active' : ''}`}
              onClick={() => handleNumberClick(num)}
            >
              {num}
            </button>
          ))}
        </div>
      </div>
      {!isSingleCellCage && (
        <div className="mb-3">
          <h6 className="form-label">Operation: <span className="fw-bold">{operation === '' ? '?' : operation}</span></h6>
          <div className="d-flex flex-wrap">
            {availableOperations.map((op) => (
              <button
                key={op}
                className={`btn btn-outline-primary m-1 ${operation === op ? 'active' : ''}`}
                onClick={() => handleOperationClick(op)}
              >
                {op}
              </button>
            ))}
          </div>
        </div>
      )}
      <p>Selected Cells: {selectedCells.length}</p>
      <div className="d-flex justify-content-end">
        <button className="btn btn-secondary me-2" onClick={onHide}>
          Cancel
        </button>
        <button className="btn btn-primary" onClick={handleSave} disabled={targetNumber === 0 || operation === ''}>
          Save Cage
        </button>
      </div>
    </CustomModal>
  );
};

export default CageModal;
